import { useSuspenseQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { userBffQueries } from 'hooks/userBffQueries';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { V3CookieService } from 'services/cookie/v3CookieService';
import { FeatureFlags } from 'utils/featureFlags';

/**
 * Single source of truth for V3 experience state.
 *
 * isExclusiveV3    — forced into V3 by LaunchDarkly.
 * tryV3            — persisted user preference from users-bff.
 * hasV3Cookie      — active V3 beta session cookie.
 * isUsingV3Beta    — active non-exclusive beta session.
 * isInV3Experience — render V3 experience right now.
 * isLoading        — true while exclusiveFlowV3 is still resolving.
 */
export function useV3ExperienceState() {
  const { [FeatureFlags.exclusiveFlowV3]: exclusiveFlowV3 } = useFeatureFlag([FeatureFlags.exclusiveFlowV3]);
  const { data: currentUser } = useSuspenseQuery(userBffQueries.currentUser);

  const isLoading = exclusiveFlowV3 === undefined;
  const isExclusiveV3 = exclusiveFlowV3 === true;
  const tryV3 = currentUser?.tryV3 ?? false;
  const hasV3Cookie = V3CookieService.hasV3Experience();
  const isUsingV3Beta = !isExclusiveV3 && tryV3 && hasV3Cookie;
  const isInV3Experience = isExclusiveV3 || isUsingV3Beta;

  useEffect(() => {
    if (isLoading || currentUser === null) {
      return;
    }

    if (!isExclusiveV3 && !tryV3 && hasV3Cookie) {
      V3CookieService.clearV3Experience();
    }
  }, [currentUser, hasV3Cookie, isExclusiveV3, isLoading, tryV3]);

  return { isExclusiveV3, tryV3, hasV3Cookie, isUsingV3Beta, isInV3Experience, isLoading };
}
